import apiClient from './client';

export interface PortalUser {
  id: number;
  email: string;
  username: string;
  role: 'employee' | 'client';
  phone?: string;
}

export interface ScheduleSubtask {
  id: number;
  title: string;
  approval_status?: string;
  suggested_by?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  status?: string;
}

export interface ScheduleEvent {
  id: number;
  job_id: number;
  job_title: string;
  site_address?: string;
  scheduled_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  status: string;
  notes?: string;
  crew_name?: string;
  /** Day-level tasks, same as the admin calendar shows. */
  subtasks?: ScheduleSubtask[];
}

export interface PortalJob {
  id: number;
  title: string;
  status: string;
  site_address?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  budget?: number;
}

export interface PortalDocument {
  id: number;
  job_id: number;
  job_title?: string;
  file_name?: string;
  /** Office-assigned display label; file_name remains the original. */
  label?: string | null;
  file_path?: string;
  file_type?: string;
  title?: string;
  document_type?: string;
  document_category?: string;
  status?: string;
  created_at: string;
  uploaded_at?: string;
  /** 1 when the row is a site photo (drives the Photos sub-group, CR #157). */
  is_photo?: number;
}

export interface TimeEntry {
  id: number;
  job_id?: number;
  job_title?: string;
  work_date: string;
  hours: number;
  break_minutes: number;
  notes?: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  source: 'portal' | 'sms' | 'admin';
  submitted_at?: string;
  start_time?: string | null;
  end_time?: string | null;
  disputed?: number | boolean;
  dispute_note?: string | null;
  lines?: TimeEntryLine[];
}

export interface TimeEntryLine {
  id?: number;
  job_id?: number | null;
  job_title?: string;
  description?: string | null;
  hours: number;
  display_order?: number;
}

export interface AdminTimeEntryInput {
  job_id?: number | null;
  work_date: string;
  hours: number;
  break_minutes?: number;
  notes?: string;
  start_time?: string | null;
  end_time?: string | null;
  lines?: { job_id?: number | null; description?: string; hours: number }[];
}

export interface NotificationPrefs {
  email_enabled: boolean;
  sms_enabled: boolean;
  rcs_enabled: boolean;
  hours_reminder_enabled: boolean;
  invoice_notifications: boolean;
  schedule_notifications: boolean;
  document_notifications: boolean;
  payment_reminders: boolean;
}

export interface ClientProfile {
  id: number;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  address?: string;
  billing_city?: string;
  billing_province?: string;
  billing_postal?: string;
  site_address?: string;
  preferred_contact?: string;
  how_heard?: string;
  onboarding_complete?: boolean;
}

// Auth
export const portalLogin = async (email: string, password: string) => {
  const { data } = await apiClient.post('/portal/auth/login', { email, password });
  return data.data as { user: PortalUser; accessToken: string; refreshToken: string };
};

export const portalRegister = async (code: string, password: string, username?: string) => {
  const { data } = await apiClient.post('/portal/auth/register', { code, password, username });
  return data.data as { user: PortalUser; accessToken: string; refreshToken: string };
};

export const getPortalGoogleUrl = async (): Promise<string> => {
  const { data } = await apiClient.get('/portal/auth/google/url');
  return data.url;
};

// Profile
export const getPortalMe = async (): Promise<PortalUser> => {
  const { data } = await apiClient.get('/portal/me');
  return data.data;
};

// Schedule
export const getPortalSchedule = async (): Promise<ScheduleEvent[]> => {
  const { data } = await apiClient.get('/portal/me/schedule');
  return data.data;
};

// Jobs
export const getPortalJobs = async (): Promise<PortalJob[]> => {
  const { data } = await apiClient.get('/portal/me/jobs');
  return data.data;
};

// Client home overview (CR #135) — progress, next work day, activity feed
export interface ClientJobProgress {
  id: number; title: string; status: string;
  start_date: string | null; end_date: string | null;
  steps_total: number; steps_done: number; steps_active: number;
  photo_count: number; next_visit_date: string | null;
  next_step_title: string | null; last_update_date: string | null;
}
export interface ClientActivityItem {
  kind: 'photos' | 'log' | 'task_done' | 'invoice' | 'quote';
  job_id: number | null; job_title: string | null; date: string | null;
  count: number; body: string | null; ref_id: number | null; label: string | null;
}
export interface ClientOverview {
  jobs: ClientJobProgress[];
  activity: ClientActivityItem[];
  photos: { id: number; job_id: number; url: string; caption: string | null }[];
}
export const getClientOverview = async (): Promise<ClientOverview> => {
  const { data } = await apiClient.get('/portal/me/overview');
  return data.data;
};

// Documents
export const getPortalDocuments = async (): Promise<PortalDocument[]> => {
  const { data } = await apiClient.get('/portal/me/documents');
  return data.data;
};

// Notification prefs
export const getNotificationPrefs = async (): Promise<NotificationPrefs> => {
  const { data } = await apiClient.get('/portal/me/notification-prefs');
  return data.data;
};

export const updateNotificationPrefs = async (prefs: Partial<NotificationPrefs>): Promise<void> => {
  await apiClient.put('/portal/me/notification-prefs', prefs);
};

// Time entries
export const getTimeEntries = async (): Promise<TimeEntry[]> => {
  const { data } = await apiClient.get('/portal/me/time-entries');
  return data.data;
};

export const createTimeEntry = async (entry: {
  work_date: string;
  hours: number;
  break_minutes?: number;
  notes?: string;
  job_id?: number;
  submit?: boolean;
}): Promise<{ id: number }> => {
  const { data } = await apiClient.post('/portal/me/time-entries', entry);
  return data.data;
};

export const updateTimeEntry = async (id: number, entry: {
  hours?: number;
  break_minutes?: number;
  notes?: string;
  job_id?: number;
}): Promise<void> => {
  await apiClient.put(`/portal/me/time-entries/${id}`, entry);
};

export const submitTimeEntry = async (id: number): Promise<void> => {
  await apiClient.post(`/portal/me/time-entries/${id}/submit`);
};

// Paystubs (employee portal)
export interface PortalPaystub {
  id: number;
  pay_period_start: string;
  pay_period_end: string;
  pay_date: string;
  pay_frequency?: string;
  regular_hours: number;
  ot_1_5_hours: number;
  ot_2_0_hours: number;
  hourly_rate: number;
  regular_pay?: number;
  ot_1_5_pay?: number;
  ot_2_0_pay?: number;
  gross_pay: number;
  cpp: number;
  ei: number;
  federal_tax: number;
  provincial_tax: number;
  total_deductions: number;
  net_pay: number;
  ytd_gross: number;
}

export const getPortalPaystubs = async (): Promise<PortalPaystub[]> => {
  const { data } = await apiClient.get('/portal/me/paystubs');
  return data.data;
};

// Invoices (client portal)
export interface PortalInvoice {
  id: number;
  invoice_number: string;
  job_title?: string;
  status: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  due_date?: string;
  invoice_date?: string;
  created_at: string;
}

export const getPortalInvoices = async (): Promise<PortalInvoice[]> => {
  const { data } = await apiClient.get('/portal/me/invoices');
  return data.data;
};

export const getPortalInvoicePaymentLink = async (invoiceId: number): Promise<{ url: string }> => {
  const { data } = await apiClient.get(`/portal/me/invoices/${invoiceId}/payment-link`);
  return { url: data.data.url };
};

// Profile
export const updatePortalProfile = async (data: { username?: string; phone?: string }): Promise<void> => {
  await apiClient.put('/portal/me/profile', data);
};

export const changePortalPassword = async (current_password: string, new_password: string): Promise<void> => {
  await apiClient.post('/portal/me/change-password', { current_password, new_password });
};

export const getClientProfile = async (): Promise<ClientProfile | null> => {
  const { data } = await apiClient.get('/portal/me/client-profile');
  return data.data;
};

export const updateClientProfile = async (profile: Partial<ClientProfile>): Promise<void> => {
  await apiClient.put('/portal/me/client-profile', profile);
};

// Admin: all time entries + approve/reject
export const getAllTimeEntries = async (): Promise<(TimeEntry & { employee_email: string; employee_name: string })[]> => {
  const { data } = await apiClient.get('/portal/time-entries');
  return data.data;
};

// Scheduled job-days with no hours booked to that job (gap report).
export interface ScheduledUnlogged {
  work_date: string;
  job_id: number;
  job_title: string;
  status: string;
  crew_name: string | null;
  start_time: string | null;
  end_time: string | null;
  workers: string[];
}

export const getScheduledUnlogged = async (days?: number): Promise<ScheduledUnlogged[]> => {
  const qs = days != null ? `?days=${days}` : '';
  const { data } = await apiClient.get(`/portal/scheduled-unlogged${qs}`);
  return data.data;
};

// Mark a whole day as "no work performed" (clears its scheduled gaps).
export const markNoWorkDay = async (work_date: string, note?: string): Promise<void> => {
  await apiClient.post('/portal/no-work-days', { work_date, note });
};

export const clearNoWorkDay = async (work_date: string): Promise<void> => {
  await apiClient.delete(`/portal/no-work-days/${work_date}`);
};

export const approveTimeEntry = async (id: number): Promise<void> => {
  await apiClient.post(`/portal/time-entries/${id}/approve`);
};

export const rejectTimeEntry = async (id: number): Promise<void> => {
  await apiClient.post(`/portal/time-entries/${id}/reject`);
};

// ── Admin-side time entry for a specific employee (admin/pm/owner) ─────────
export const getEmployeeTimeEntries = async (employeeId: number | string): Promise<TimeEntry[]> => {
  const { data } = await apiClient.get(`/portal/time-entries/employee/${employeeId}`);
  return data.data;
};

export const createEmployeeTimeEntry = async (
  employeeId: number | string,
  entry: AdminTimeEntryInput,
): Promise<{ id: number }> => {
  const { data } = await apiClient.post(`/portal/time-entries/employee/${employeeId}`, entry);
  return data.data;
};

export const updateAdminTimeEntry = async (
  id: number,
  entry: Partial<AdminTimeEntryInput> & { status?: string },
): Promise<void> => {
  await apiClient.put(`/portal/time-entries/${id}`, entry);
};

export const deleteAdminTimeEntry = async (id: number): Promise<void> => {
  await apiClient.delete(`/portal/time-entries/${id}`);
};

// Worker pay summary (earned / paid / owed) for the employee portal.
export interface MyPay {
  summary: {
    hourly_rate: number; approved_hours: number; pending_hours: number;
    earned: number; paid: number; owed: number;
  };
  payouts: { id: number; amount: number; paid_on: string; method: string; notes?: string | null }[];
}

export const getMyPay = async (): Promise<MyPay | null> => {
  const { data } = await apiClient.get('/portal/me/pay');
  return data.data;
};

// Full job detail for the employee portal (tasks, schedule, gated documents).
export interface PortalJobDetail {
  job: {
    id: number; title: string; status: string; site_address?: string;
    site_city?: string; site_province?: string; description?: string;
    client_notes?: string; safety_notes?: string; notes?: string;
    start_date?: string; end_date?: string;
  };
  tasks: { id: number; title: string; status: string; priority?: string; description?: string; assigned_to_name?: string }[];
  schedule: { id: number; scheduled_date: string; end_date?: string; start_time?: string; end_time?: string; status: string; crew_name?: string }[];
  documents: { id: number; file_name?: string; title?: string; file_path?: string; document_category?: string; created_at: string }[];
}

export const getPortalJob = async (jobId: number): Promise<PortalJobDetail> => {
  const { data } = await apiClient.get(`/portal/me/jobs/${jobId}`);
  return data.data;
};

// Worker: dispute a submitted/approved entry; Admin: clear the dispute.
export const disputeMyTimeEntry = async (id: number, note: string): Promise<void> => {
  await apiClient.post(`/portal/me/time-entries/${id}/dispute`, { note });
};

export const resolveTimeEntryDispute = async (id: number): Promise<void> => {
  await apiClient.post(`/portal/time-entries/${id}/resolve-dispute`);
};
